<script setup lang="ts">
import { ComboboxAnchor, ComboboxInput, ComboboxRoot } from 'radix-vue'
import TagItem from './TagItem.vue'
import { TagsInput, TagsInputInput } from '@/components/ui/tags-input'
import { useTagsQuery } from '~~/src/composables/query/tags/useTagsQuery'
import type { TagData } from '~~/src/api/tags/types'
import { useCreateTagMutation } from '~~/src/composables/mutations/tags/useCreateTagMutation'
import { useElementalTagsQuery } from '~~/src/composables/query/tags/useElementalTagsQuery'
import { useInsertElementalInATag } from '~~/src/composables/mutations/tags/useInsertElementalInATagsMutation'
import type { Elemental } from '~~/src/types/elemental'
const props = defineProps<Props>()

const { colors } = useTheme()

defineOptions({
  name: 'TagMenu',
})
interface Props {
  open?: boolean
  elemental: Elemental
}

const productId = computed(() => props.elemental.id)
const searchTerm = ref('')

const user = useUser()
const toast = useToast()
const userId = computed(() => user.value?.id)

const { data: allTags, isLoading: isLoadingUserTags, refetch } = useTagsQuery({
  userId,
}, {
  enabled: computed(() => !!user.value?.id),
})

const { data: elementalTags, isLoading: isLoadingElementalTags, refetch: refetchElementalTags } = useElementalTagsQuery({
  elementalId: productId,
}, {
  enable: computed(() => !!props.elemental.id),
})

watch(props.open, (openValue) => {
  if (openValue && !!props.elemental.id && !!user.value?.id) {
    refetch()
    refetchElementalTags()
  }
})

const isLoading = computed(() => isLoadingUserTags.value || isLoadingElementalTags.value)

const { mutate: createTag } = useCreateTagMutation()
const { mutate: setElementalTag } = useInsertElementalInATag()
const filteredTags = computed(() =>
  !allTags.value
    ? []
    : allTags.value
      .filter(tag => !elementalTags.value?.some(t => t.title === tag.title))
      .filter(tag => (tag?.title ?? '').toLowerCase().includes((searchTerm.value ?? '').toLowerCase())),
)

const getTagColor = (): string => {
  return getRandomColors()[1]
}

const addToTagList = async (item: TagData) => {
  setElementalTag({
    elementalId: productId.value,
    tagId: item.id,
    tag: item,
  })
}

const renamingTag = ref<string | null>()
const handleNewTag = async (payload: unknown[]) => {
  searchTerm.value = ''
  const newTags = payload as string[]

  for (const newTag of newTags) {
    const existingTag = allTags.value?.find(tag => tag.title.trim() === newTag.trim())

    if (existingTag?.id) {
      setElementalTag({
        elementalId: productId.value,
        tagId: existingTag.id,
        tag: existingTag,
      })
      continue
    }
    const tag = {
      id: randomID(),
      title: newTag.trim(),
      color: getTagColor(),
    }
    createTag({
      tag,
      userId: userId.value,
      elementalId: productId.value,
    },
    {
      onSuccess(newTag: TagData | null) {
        if (newTag) {
          setElementalTag({
            elementalId: productId.value,
            tagId: newTag.id,
            tag: newTag,
          })
        }
      },
      onError(err) {
        toast.error(err.message || 'Error creating tag')
      },
    },
    )
  }
}

const toggleEditTag = (id?: string | null) => {
  renamingTag.value = id
}
</script>

<template>
  <div class="w-[320px] px-[6px] pt-[6px] tag-menu">
    <p class="text-darkGray font-500 text-12px pb-1">
      <span>Item Tags:</span>
    </p>
    <ComboboxRoot v-model:search-term="searchTerm" :disabled="isLoading">
      <ComboboxAnchor>
        <TagsInput add-on-paste delimiter="," @update:model-value="handleNewTag">
          <TagItem
            v-for="(item) in elementalTags"
            :key="item.id"
            :value="item.title"
            :style="{ backgroundColor: item.color }"
            class="group relative tag-menu-item justify-center"
            :is-editing="renamingTag === item.id"
            :tag="item"
            :elemental-id="elemental?.id ?? null"
            @edit="toggleEditTag($event)"
            @close-edit="toggleEditTag(null)"
          />

          <div v-if="isLoading && !elementalTags?.length" animate-spin :style="{ animationDuration: '2s' }">
            <IconSpinner :size="20" :color="colors.darkerGray" />
          </div>
          <ComboboxInput v-else as-child>
            <TagsInputInput class="font-500" placeholder="Enter tag" maxlength="100" />
          </ComboboxInput>
        </TagsInput>
      </ComboboxAnchor>
    </ComboboxRoot>

    <p class="text-darkGray font-500 text-12px pt-4">
      <span v-if="allTags && allTags.length > 0">All Tags ({{ filteredTags.length }}):</span>
      <span v-else>No Tags Created</span>
    </p>
    <TagsInput class="!border-0 !px-0">
      <TagItem
        v-for="item in filteredTags"
        :key="item.id"
        :is-editing="renamingTag === item.id"
        :tag="item"
        @edit="toggleEditTag($event)"
        @close-edit="toggleEditTag(null)"
        @selected="addToTagList(item)"
      />
    </TagsInput>
  </div>
</template>

<style lang="scss">
.tag-menu {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 12px;

  .tag-menu-item {
    min-width: 45px;

    input {
      width: min-content;
      border: none;
      outline: none;
      background: transparent;
      padding: 0 0.5rem;
    }
  }
}
</style>
